import { MutationCache, QueryCache, QueryClient, QueryClientProvider, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { expireSession, getSessionToken, isUnauthorizedError, onSessionExpired, setSessionToken } from "../shared/session/auth-token";
import { readOidcTicket, redeemOidcTicket } from "../features/auth/model/oidc-sign-in";
import { DashboardPage } from "../pages/dashboard/ui/dashboard-page";
import { PublicFormPage } from "../pages/form/ui/public-form-page";
import { PublicViewPage } from "../pages/view/ui/public-view-page";
import { ConnectPage } from "../pages/connect/ui/connect-page";
import { LoginPage } from "../pages/login/ui/login-page";
import { TooltipProvider } from "../components/ui/tooltip";
import { TRPCProvider, trpc, useTRPC } from "../shared/api/trpc";
import { I18nProvider, useT } from "../shared/i18n/i18n";
import { AppErrorBoundary } from "../shared/ui/app-error-boundary";

export function App() {
  // Любой запрос или изменение, на которое сервер ответил «вы не вошли»,
  // завершает сессию в браузере. Ловить это надо здесь, одним местом: иначе
  // каждый экран показывал бы протухшую сессию по-своему, а главный — сырым
  // «UNAUTHORIZED» вместо экрана входа.
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        // Повтор запроса с мёртвой сессией даст тот же отказ — только позже.
        retry: (failureCount, error) => !isUnauthorizedError(error) && failureCount < 3,
        staleTime: 30 * 1000,
      },
    },
    mutationCache: new MutationCache({
      onError: (error) => {
        if (isUnauthorizedError(error)) expireSession();
      },
    }),
    queryCache: new QueryCache({
      onError: (error) => {
        if (isUnauthorizedError(error)) expireSession();
      },
    }),
  }));
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider queryClient={queryClient} trpcClient={trpc}>
        <I18nProvider>
          <AppErrorBoundary fallback={(retry) => <AppCrash onRetry={retry} />}>
            <AppContent />
          </AppErrorBoundary>
        </I18nProvider>
      </TRPCProvider>
    </QueryClientProvider>
  );
}

// Экран после сбоя. Он должен сказать, что произошло, и дать дорогу дальше —
// перезагрузка страницы теряет всё, что человек не успел сохранить, поэтому
// сначала предлагается просто попробовать ещё раз.
function AppCrash({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3 p-8 text-center">
      <h1 className="text-base font-medium">{t("crash.title")}</h1>
      <p className="max-w-sm text-sm text-muted-foreground">{t("crash.description")}</p>
      <div className="flex gap-2">
        <button
          className="rounded-md border border-border px-3 py-1.5 text-sm transition-colors hover:bg-accent/50"
          onClick={onRetry}
          type="button"
        >
          {t("crash.retry")}
        </button>
        <button
          className="rounded-md border border-border px-3 py-1.5 text-sm transition-colors hover:bg-accent/50"
          onClick={() => window.location.reload()}
          type="button"
        >
          {t("crash.reload")}
        </button>
      </div>
    </div>
  );
}

function AppContent() {
  const trpc = useTRPC();
  const { mutateAsync: acceptInvitation } = useMutation(trpc.access.acceptInvitation.mutationOptions());
  const [isSignedIn, setIsSignedIn] = useState(() => Boolean(getSessionToken()));
  const queryClient = useQueryClient();
  // Сессия истекла — назад на вход. Кэш очищается: данные прежней сессии не
  // должны мелькнуть после повторного входа, возможно — под другим человеком.
  useEffect(
    () =>
      onSessionExpired(() => {
        queryClient.clear();
        setIsSignedIn(false);
      }),
    [queryClient],
  );
  // Возврат от провайдера входа: одноразовый билет во фрагменте адреса
  // меняется на сессию. Фрагмент стирается сразу — билет не должен остаться в
  // истории браузера.
  const t = useT();
  const [oidcTicket] = useState(() => readOidcTicket(window.location.hash));
  const [isRedeeming, setIsRedeeming] = useState(oidcTicket !== null);
  const redeemStarted = useRef(false);
  useEffect(() => {
    if (!oidcTicket || redeemStarted.current) return;
    redeemStarted.current = true;
    window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
    void redeemOidcTicket(oidcTicket)
      .then((sessionToken) => {
        queryClient.clear();
        setSessionToken(sessionToken);
        setIsSignedIn(true);
      })
      .catch(() => {
        const search = new URLSearchParams(window.location.search);
        search.set("login_error", "expired");
        window.history.replaceState({}, "", `${window.location.pathname}?${search.toString()}`);
      })
      .finally(() => setIsRedeeming(false));
  }, [oidcTicket, queryClient]);
  const invitationToken = invitationTokenFromPath(window.location.pathname);
  const formToken = formTokenFromPath(window.location.pathname);
  const viewShareToken = viewShareTokenFromPath(window.location.pathname);
  // An app asked to connect. The decision belongs to a signed-in person, so an
  // anonymous visitor sees the ordinary sign-in screen and lands back here.
  const connectRequestId = connectRequestFromLocation(window.location);
  const [invitationError, setInvitationError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSignedIn || !invitationToken) return;
    void acceptInvitation({ token: invitationToken }).then(() => {
      window.history.replaceState({}, "", "/");
      window.location.reload();
    }).catch((error) => {
      setInvitationError(error instanceof Error ? error.message : "Could not accept invitation.");
    });
  }, [acceptInvitation, invitationToken, isSignedIn]);

  if (isRedeeming) {
    return <main className="grid min-h-screen place-items-center text-sm">{t("login.redeeming")}</main>;
  }

  // Public intake forms render for anyone — the capability token in the URL
  // is the whole authorization, no session involved.
  if (formToken) {
    return (
      <TooltipProvider>
        <PublicFormPage token={formToken} />
      </TooltipProvider>
    );
  }

  // Read-only shared board views work the same way: capability URL, no session.
  if (viewShareToken) {
    return (
      <TooltipProvider>
        <PublicViewPage token={viewShareToken} />
      </TooltipProvider>
    );
  }

  if (connectRequestId && isSignedIn) {
    return (
      <TooltipProvider>
        <ConnectPage requestId={connectRequestId} />
      </TooltipProvider>
    );
  }

  const content = isSignedIn && invitationToken
    ? <main className="grid min-h-screen place-items-center text-sm">{invitationError ?? "Joining workspace..."}</main>
    : !isSignedIn
      ? (
        <TooltipProvider>
          <LoginPage invitationToken={invitationToken ?? undefined} onSignedIn={() => setIsSignedIn(true)} />
        </TooltipProvider>
      )
      : (
        <TooltipProvider>
          <DashboardPage onSignedOut={() => setIsSignedIn(false)} />
        </TooltipProvider>
      );

  return content;
}

function connectRequestFromLocation(location: Location) {
  if (location.pathname !== "/connect") return null;
  const requestId = new URLSearchParams(location.search).get("request");
  return requestId && requestId.length > 0 ? requestId : null;
}

function invitationTokenFromPath(pathname: string) {
  const match = pathname.match(/^\/invite\/([^/]+)$/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function formTokenFromPath(pathname: string) {
  const match = pathname.match(/^\/form\/([^/]+)$/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function viewShareTokenFromPath(pathname: string) {
  const match = pathname.match(/^\/view\/([^/]+)$/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}
